import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import "../css/Login.css";

const EditCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const server_url = localStorage.getItem("server_url");
  const grage_id = localStorage.getItem("grage_id");
  const company_id = localStorage.getItem("company_id");
  const garageData = JSON.parse(localStorage.getItem("garageData") || "{}");

  // Get customer data from navigation state
  const customerData = location.state?.customer;

  // Searchable Dropdown Component (same as above)
  const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder,
    name,
    error,
    loading,
    disabled,
    required,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }, [searchTerm, options]);

    useEffect(() => {
      const selected = options.find((opt) => opt.value == value);
      setSearchTerm(selected ? selected.label : "");
    }, [value, options]);

    const handleInputChange = (e) => {
      const term = e.target.value;
      setSearchTerm(term);
      setIsOpen(true);
    };

    const handleSelect = (selectedValue) => {
      setIsOpen(false);
      if (onChange) {
        onChange({ target: { name, value: selectedValue } });
      }
    };

    const displayValue = options.find((opt) => opt.value == value)?.label || "";
    const inputValue = isOpen ? searchTerm : displayValue;

    return (
      <div className="searchable-dropdown" ref={dropdownRef}>
        <div className={`input-group ${error ? "error" : ""}`}>
          <div className="input-icon">
            <i className="fas fa-search"></i>
          </div>
          <input
            type="text"
            className="form-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled || loading}
            readOnly={!isOpen}
            required={required}
          />
          {isOpen && (
            <ul
              className="dropdown-list"
              style={{ width: "70%", textAlign: "left" }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    style={{
                      padding: "5px",
                      margin: "2px",
                    }}
                    key={option.value}
                    className="dropdown-item"
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li className="dropdown-item no-results">No results found</li>
              )}
            </ul>
          )}
        </div>
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}
        {loading && <div className="loading-message">Loading...</div>}
      </div>
    );
  };

  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "",
    customer_mobile: "",
    customer_email: "",
    customer_area: "",
    customer_address: "",
    state_id: garageData.grage_state_id || "",
    city_id: garageData.grage_city_id || "",
    customer_pincode: garageData.grage_pincode || "",
    customer_company_name: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const [cityLoading, setCityLoading] = useState(false);

  // New state for customer vehicles
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  // Populate form with customer data when component loads
  useEffect(() => {
    if (customerData) {
      setFormData({
        customer_id: customerData.customer_id || "",
        customer_name: customerData.customer_name || "",
        customer_mobile: customerData.customer_mobile || "",
        customer_email: customerData.customer_email || "",
        customer_area: customerData.customer_area || "",
        customer_address: customerData.customer_address || "",
        state_id: customerData.state_id || garageData.grage_state_id || "",
        city_id: customerData.city_id || garageData.grage_city_id || "",
        customer_pincode:
          customerData.customer_pincode || garageData.grage_pincode || "",
        customer_company_name: customerData.customer_company_name || "",
      });

      // Fetch customer vehicles when customer data is available
      if (customerData.customer_id) {
        fetchCustomerVehicles(customerData.customer_id);
      }
    }
  }, [customerData]);

  // Fetch customer vehicles
  const fetchCustomerVehicles = async (customerId) => {
    if (!server_url || !customerId) return;

    setVehiclesLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id || "2");
      formData.append("customer_id", customerId);
      formData.append("vehicle_grage_id", grage_id || "1");
      formData.append("vehicle_reg_no", "");

      const response = await axios.post(`${server_url}vehicle.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status == "1") {
        setCustomerVehicles(response.data.result || []);
      } else {
        setCustomerVehicles([]);
      }
    } catch (error) {
      console.error("Fetch customer vehicles error:", error);
      setCustomerVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      if (!server_url) {
        toast.error("Server URL not configured.");
        return;
      }
      setStateLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id || "2");
        const response = await axios.post(
          `${server_url}state.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );
        if (response.data.status == "1") {
          const stateOptions = (response.data.result || []).map((state) => ({
            value: state.state_id,
            label: state.state_name,
          }));
          setStates(stateOptions);
        } else {
          toast.error(response.data.error || "Failed to fetch states");
        }
      } catch (error) {
        console.error("Fetch states error:", error);
        toast.error("Unable to fetch states. Please try again.");
      } finally {
        setStateLoading(false);
      }
    };
    fetchStates();
  }, [server_url, company_id]);

  // Fetch cities when state_id changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.state_id || !company_id) {
        setCities([]);
        return;
      }
      setCityLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id);
        formDataToSend.append("state_id", formData.state_id);
        const response = await axios.post(
          `${server_url}city.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );
        if (response.data.status == "1") {
          const cityOptions = (response.data.result || []).map((city) => ({
            value: city.city_id,
            label: city.city_name,
          }));
          setCities(cityOptions);
        } else {
          setCities([]);
        }
      } catch (error) {
        console.error("Fetch cities error:", error);
        setCities([]);
      } finally {
        setCityLoading(false);
      }
    };
    fetchCities();
  }, [formData.state_id, server_url, company_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = "Customer name is required";
    }
    if (!formData.customer_mobile.trim()) {
      newErrors.customer_mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.customer_mobile)) {
      newErrors.customer_mobile = "Please enter a valid 10-digit mobile number";
    }
    if (
      formData.customer_email &&
      !/\S+@\S+\.\S+/.test(formData.customer_email)
    ) {
      newErrors.customer_email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const customerPayload = {
        company_id: company_id,
        customer_id: formData.customer_id,
        customer_name: formData.customer_name,
        customer_mobile: formData.customer_mobile,
        customer_email: formData.customer_email,
        customer_area: formData.customer_area,
        customer_address: formData.customer_address,
        state_id: formData.state_id,
        city_id: formData.city_id,
        customer_pincode: formData.customer_pincode,
        customer_company_name: formData.customer_company_name,
      };
      const customerFormData = new URLSearchParams();
      for (const key in customerPayload) {
        customerFormData.append(key, customerPayload[key]);
      }
      customerFormData.append("customer_grage_id", grage_id || "");

      const customerResponse = await axios.post(
        `${server_url}update_customer.php`,
        customerFormData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (customerResponse.data.status == "0") {
        toast.error(customerResponse.data.error);
      } else {
        toast.success("Customer updated successfully!");
        navigate("/customers", { replace: true });
      }
    } catch (error) {
      console.error("Update customer error:", error);
      toast.error("Unable to update customer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle vehicle edit
  const handleEditVehicle = (vehicle) => {
    navigate("/edit-vehicle", { state: { vehicle } });
  };

  // Handle add new vehicle
  const handleAddVehicle = () => {
    navigate("/add-customer-vehicle", {
      state: {
        customer: {
          customer_id: formData.customer_id,
          customer_name: formData.customer_name,
          customer_mobile: formData.customer_mobile,
        },
      },
    });
  };

  if (!customerData) {
    return (
      <div className="login-container">
        <div className="login-form-container">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h3>No customer data found</h3>
            <p>Please go back and select a customer to edit.</p>
            <Link
              to="/customers"
              style={{ color: "#667eea", textDecoration: "none" }}
            >
              <i className="fas fa-arrow-left"></i> Back to Customers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {/* Customer Vehicles Horizontal Scroll */}
          <div
            style={{
              marginBottom: "10px",
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              padding: "10px",
              boxShadow: "3px 3px 20px 0px #00000036",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0, color: "#2d3748", fontSize: "16px" }}>
                <i className="fas fa-car" style={{ marginRight: "8px" }}></i>
                Customer Vehicles
              </h4>
            </div>

            {vehiclesLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#666",
                }}
              >
                <div
                  className="loading-spinner"
                  style={{
                    width: "30px",
                    height: "30px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #0352a1",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 10px",
                  }}
                ></div>
                Loading vehicles...
              </div>
            ) : customerVehicles.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: "15px",
                  padding: "10px 0",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#0352a1 #f1f1f1",
                }}
              >
                {customerVehicles.map((vehicle) => (
                  <div
                    key={vehicle.vehicle_id}
                    onClick={() => handleEditVehicle(vehicle)}
                    style={{
                      minWidth: "190px",
                      background: "white",
                      borderRadius: "10px",
                      padding: "12px",
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(0,0,0,0.1)";
                    }}
                  >
                    {/* Vehicle Image Section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "0px",
                      }}
                    >
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          padding: "5px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          marginRight: "10px",
                          background: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {vehicle.model_image ? (
                          <img
                            src={vehicle.model_image}
                            alt="Vehicle"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <i
                            className="fas fa-car"
                            style={{
                              fontSize: "24px",
                              color: "#0352a1",
                            }}
                          ></i>
                        )}
                      </div>

                      {/* Vehicle Details Section */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: "14px",
                            color: "#2d3748",
                            marginBottom: "4px",
                          }}
                        >
                          {vehicle.vehicle_reg_no || "N/A"}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#4a5568",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {vehicle.brand_name && (
                            <span>{vehicle.brand_name}</span>
                          )}
                          {vehicle.model_name && (
                            <>
                              - <span>{vehicle.model_name}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Edit Button */}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#666",
                  background: "#f1f5f9",
                  borderRadius: "8px",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "10px" }}>🚗</div>
                <div style={{ marginBottom: "5px", fontWeight: "500" }}>
                  No Vehicles Found
                </div>
                <div style={{ fontSize: "12px" }}>
                  This customer has no vehicles registered yet.
                </div>
                <button
                  onClick={handleAddVehicle}
                  style={{
                    background: "#0352a1",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    marginTop: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <i className="fas fa-plus"></i>
                  Add First Vehicle
                </button>
              </div>
            )}

            {/* Custom Scrollbar Styles */}
            <style jsx>{`
              div::-webkit-scrollbar {
                height: 6px;
              }
              div::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 3px;
              }
              div::-webkit-scrollbar-thumb {
                background: #0352a1;
                border-radius: 3px;
              }
              div::-webkit-scrollbar-thumb:hover {
                background: #024080;
              }
              @keyframes spin {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>

            {/* Customer Name */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Name *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-user"></i>
                </div>
                <input
                  type="text"
                  name="customer_name"
                  className={`form-input ${
                    errors.customer_name ? "error" : ""
                  }`}
                  placeholder="Customer Name *"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.customer_name && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.customer_name}
                </div>
              )}
            </div>

            {/* Customer Mobile (Disabled) */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Mobile
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-mobile-alt"></i>
                </div>
                <input
                  type="text"
                  name="customer_mobile"
                  className="form-input"
                  value={formData.customer_mobile}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            

            {/* Customer Email */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Email
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <input
                  type="email"
                  name="customer_email"
                  className={`form-input ${
                    errors.customer_email ? "error" : ""
                  }`}
                  placeholder="Customer Email"
                  value={formData.customer_email}
                  onChange={handleChange}
                />
              </div>
              {errors.customer_email && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.customer_email}
                </div>
              )}
            </div>

            {/* Customer Address */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Address
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-home"></i>
                </div>
                <input
                  type="text"
                  name="customer_address"
                  className="form-input"
                  placeholder="Customer Address"
                  value={formData.customer_address}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* State */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                State
              </label>
              <SearchableDropdown
                name="state_id"
                options={states}
                value={formData.state_id}
                onChange={handleChange}
                placeholder="Select State"
                error={errors.state_id}
                loading={stateLoading}
              />
            </div>

            {/* City */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                City
              </label>
              <SearchableDropdown
                name="city_id"
                options={cities}
                value={formData.city_id}
                onChange={handleChange}
                placeholder="Select City"
                error={errors.city_id}
                loading={cityLoading}
                disabled={!formData.state_id}
              />
            </div>

            {/* Customer Area */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Area
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <input
                  type="text"
                  name="customer_area"
                  className="form-input"
                  placeholder="Customer Area"
                  value={formData.customer_area}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Pincode */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Pincode
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-map-pin"></i>
                </div>
                <input
                  type="text"
                  name="customer_pincode"
                  className="form-input"
                  placeholder="Pincode"
                  value={formData.customer_pincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // allow only digits
                    if (value.length <= 6) {
                      handleChange(e);
                    }
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  minLength={6}
                  maxLength={6}
                />
              </div>
            </div>

            {/* Company Name */}
            <div className="form-group" style={{ display: "none" }}>
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Customer Company Name
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-building"></i>
                </div>
                <input
                  type="text"
                  name="customer_company_name"
                  className="form-input"
                  placeholder="Company Name"
                  value={formData.customer_company_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="login-button"
              disabled={loading || stateLoading}
            >
              <i className="fas fa-save"></i>
              {loading ? "Updating..." : "Update Customer"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCustomer;
